import React, { useEffect, useState } from "react";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import Loading from "../../components/loading";
import moment from "moment";
import { toast, ToastContainer } from "react-toastify";
import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchParties } from "../../redux/party/partySlice";

import {
  getSaleReport,
  ReportHistory,
  getParties,
  getPurchaseReport,
  getItemReport,
  getledger,
  getPartyReport,
  getExpense,
} from "../../services/services";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const DateInput = ({ value, onChange }) => {
  return (
    <DatePicker
     selected={value ? (isNaN(new Date(value)) ? null : new Date(value)) : null}
      onChange={(date) => onChange(moment(date).format("YYYY-MM-DD"))}
      dateFormat="dd-MM-yyyy"
      className="form-control"
      placeholderText="DD-MM-YYYY"
      // showMonthDropdown
      // showYearDropdown
      // scrollableYearDropdown
      // yearDropdownItemNumber={50}
      dropdownMode="select"
      onChangeRaw={(e) => {
        if (!e || !e.target) return;

        let inputVal = e.target.value;

        // ❗ Prevent crash when undefined/null
        if (typeof inputVal !== "string") return;

        let val = inputVal.replace(/[^0-9]/g, "");

        // Auto format DD-MM-YYYY
        if (val.length > 2) val = val.slice(0, 2) + "-" + val.slice(2);
        if (val.length > 5) val = val.slice(0, 5) + "-" + val.slice(5, 9);

        const parts = val.split("-");
        let day = parts[0];
        let month = parts[1];
        let year = parts[2];

        // Month max 12
        if (month && parseInt(month) > 12) month = "12";

        // Year validation
        if (year && year.length === 4) {
          const y = parseInt(year);
          if (y < 1000) year = "1000";
          if (y > 9999) year = "9999";
        }

        // Day validation
        if (day && month) {
          const y = year && year.length === 4 ? parseInt(year) : 2024;
          const maxDays = new Date(y, parseInt(month), 0).getDate();

          if (parseInt(day) > maxDays) {
            day = String(maxDays);
          }
        }

        let formatted = day || "";
        if (month) formatted += "-" + month;
        if (year) formatted += "-" + year;

        e.target.value = formatted;
      }}
    />
  );
};

const DateRange = ({ value, onChange }) => (
  <div className="col-12 col-md-4">
    <label className="form-label fw-semibold">Date Range</label>
    <div className="row g-2">
      <div className="col-6">
        <DateInput
          value={value.from}
          onChange={(val) => onChange("from", val)}
        />
      </div>
      <div className="col-6">
        <DateInput value={value.to} onChange={(val) => onChange("to", val)} />
      </div>
    </div>
  </div>
);

// CONFIG: Report Types & Filters
const REPORT_TYPES = {
  SALE: "Sale",
  PURCHASE: "Purchase",
  PRODUCT: "Product",
  PARTY: "Party",
  LEDGER: "Ledger",
  EXPENSE: "Expense",
};

const REPORT_ACCORDIONS = [
  {
    id: "salelist",
    title: "Sales Report",
    icon: "bi bi-cart-check",
    type: REPORT_TYPES.SALE,
  },
  {
    id: "purchaselist",
    title: "Purchases Report",
    icon: "bi bi-cart-plus",
    type: REPORT_TYPES.PURCHASE,
  },
  {
    id: "transaction",
    title: "Transactions Report",
    icon: "bi bi-arrow-left-right",
    type: REPORT_TYPES.LEDGER,
  },
  {
    id: "expense",
    title: "Expenses Report",
    icon: "bi bi-receipt",
    type: REPORT_TYPES.EXPENSE,
  },
  {
    id: "item",
    title: "Items Report",
    icon: "bi bi-box-seam",
    type: REPORT_TYPES.PRODUCT,
  },
  {
    id: "parties",
    title: "Parties Report",
    icon: "bi bi-people",
    type: REPORT_TYPES.PARTY,
  },
];

// Format filters for display under report title (top-level so DownloadsTable can use it)
const formatFilters = (filters, parties = []) => {
  if (!filters || typeof filters !== "object") return [];
  const labelMap = {
    from: "From",
    to: "To",
    status: "Status",
    partyId: "Party",
    customerId: "Customer",
    type: "Type",
    categories: "Category",
    search: "Search",
  };

  return Object.entries(filters)
    .filter(
      ([k, v]) => v !== undefined && v !== null && String(v).trim() !== "",
    )
    .map(([k, v]) => {
      const label = labelMap[k] || k.charAt(0).toUpperCase() + k.slice(1);
      // format dates
      if (k === "from" || k === "to") {
        try {
          const dt = moment(v);
          if (dt.isValid()) return `${label}: ${dt.format("DD MMM, YYYY")}`;
        } catch (e) {
          return `${label}: ${v}`;
        }
      }

      if (k === "status") {
        if (v === "paid") return `${label}: Paid`;
        if (v === "not_paid") return `${label}: Not Paid`;
        return `${label}: ${v}`;
      }

      // Resolve party/customer id to name if parties list is provided
      if (
        (k === "partyId" || k === "customerId") &&
        parties &&
        Array.isArray(parties)
      ) {
        const found = parties.find((p) => String(p._id) === String(v));
        if (found) return `${label}: ${found.name}`;
      }

      return `${label}: ${v}`;
    });
};

// Produce a compact inline representation for filters, e.g. "Not Paid | Zoya |"
const formatFiltersInline = (filters, parties = []) => {
  const raw = formatFilters(filters, parties);
  if (!raw || !raw.length) return "";

  // Extract label/value and categorize
  let customer = null;
  let status = null;
  let fromDate = null;
  let toDate = null;
  const others = [];

  raw.forEach((item) => {
    const idx = item.indexOf(":");
    if (idx !== -1) {
      const label = item.slice(0, idx).trim();
      const value = item.slice(idx + 1).trim();
      if (label === "Customer" || label === "Party") {
        customer = value;
        return;
      }
      if (label === "Status") {
        status = value;
        return;
      }
      if (label === "From") {
        fromDate = value;
        return;
      }
      if (label === "To") {
        toDate = value;
        return;
      }
      // For other labels, include label:value compactly
      others.push(`${label}: ${value}`);
    } else {
      others.push(item);
    }
  });

  // Order: Customer (Party) first, then Status, then others
  const ordered = [];
  if (customer) ordered.push(customer);
  if (status) ordered.push(status);
  // if date range present, render compactly as `start to end` (no 'From/To' labels)
  if (fromDate || toDate) {
    if (fromDate && toDate) ordered.push(`${fromDate} to ${toDate}`);
    else if (fromDate) ordered.push(fromDate);
    else ordered.push(toDate);
  }
  if (others.length) ordered.push(...others);

  // join with pipes; do NOT append trailing pipe (clean output)
  return ordered.join(" | ");
};

function Reports() {
  const dispatch = useDispatch();
  const { list: parties = [] } = useSelector((state) => state.party);
  const [activeTab, setActiveTab] = useState("reports");
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [downloads, setDownloads] = useState([]);

  // Unified filter state (cleaner than 7 separate states)
  const [filters, setFilters] = useState({
    sale: { from: "", to: "", status: "", partyName: "" },
    purchase: { from: "", to: "", partyId: "", mode: "", status: "" },
    product: { from: "", to: "", status: "" },
    party: { name: "", partyType: "", partyId: "" },
    ledger: { from: "", to: "", partyId: "", type: "" },
    expense: { from: "", to: "", categories: "" },
  });

  useEffect(() => {
    const init = async () => {
      await fetchReportHistory();
      dispatch(fetchParties({ page: 1, limit: 500 }));
    };
    init();
  }, [dispatch]);

  const fetchReportHistory = async () => {
    try {
      setLoading(true);
      const res = await ReportHistory();
      if (res.data.status && res.data.response?.length > 0) {
        const formatted = res.data.response.map((r) => {
          const plural = toPluralType(r.type);
          const title = `${
            plural.charAt(0).toUpperCase() + plural.slice(1)
          } Report`;
          return {
            _id: r.id,
            title,
            fileName: r.fileName,
            category: plural.charAt(0).toUpperCase() + plural.slice(1),
            date: r.createdAt,
            status: "Completed",
            filters: r.filters,
          };
        });
        setDownloads(formatted);
      }
    } catch (err) {
      console.error("Failed to load report history", err);
      toast.error("Failed to load downloads");
    } finally {
      setLoading(false);
    }
  };

  const toPluralType = (type) => {
    if (!type) return "reports";
    const t = String(type).toLowerCase();
    const map = {
      sale: "sales",
      sales: "sales",
      purchase: "purchases",
      purchases: "purchases",
      item: "items",
      items: "items",
      transaction: "transactions",
      transactions: "transactions",
      expense: "expenses",
      expenses: "expenses",
      party: "parties",
      parties: "parties",
      ledger: "ledgers",
      ledgers: "ledgers",
    };
    for (const k of Object.keys(map)) if (t.includes(k)) return map[k];
    return t.endsWith("s") ? t : `${t}s`;
  };
  // Report Generation Handlers
  const generateReport = async (type) => {
    const handlers = {
      [REPORT_TYPES.SALE]: handleGenerateSaleReport,
      [REPORT_TYPES.PURCHASE]: handleGeneratePurchaseReport,
      [REPORT_TYPES.PRODUCT]: handleGenerateProductReport,
      [REPORT_TYPES.PARTY]: handleGeneratePartyReport,
      [REPORT_TYPES.LEDGER]: handleGenerateLedgerReport,
      [REPORT_TYPES.EXPENSE]: handleGenerateExpenseReport,
    };

    const handler = handlers[type];
    if (handler) await handler();
  };

  const addToDownloads = (response) => {
    const inferTypeFromFile = (fn) => {
      if (!fn) return null;
      const m = fn.match(/^([A-Za-z]+)[_-]/);
      if (m && m[1]) return m[1];
      const nameOnly = fn.replace(/\.csv$/i, "");
      return nameOnly || null;
    };

    const rawType =
      response.type || inferTypeFromFile(response.fileName) || "reports";
    const plural = toPluralType(rawType);
    const normalized = plural.charAt(0).toUpperCase() + String(plural).slice(1);

    const newReport = {
      _id: response.id || Date.now().toString(),
      title: `${normalized} Report`,
      fileName: response.fileName,
      category: normalized,
      date: response.createdAt || new Date().toISOString(),
      status: "Completed",
      filters: response.filters,
    };
    setDownloads((prev) => [newReport, ...prev]);
  };

  const triggerDownload = (csvContent, fileName) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "report.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Individual report generators (clean & reusable pattern)
  const handleGenerateSaleReport = async () => {
    setLoading(true);
    try {
      const params = {};
      const f = filters.sale;
      if (f.partyName) params.customerId = f.partyName;
      if (f.from) params.from = f.from;
      if (f.to) params.to = f.to;
      if (f.status === "paid") params.status = "paid";
      if (f.status === "not_paid") params.status = "not_paid";

      const res = await getSaleReport(params);
      if (res.data.status) {
        triggerDownload(res.data.response.csv, res.data.response.fileName);
        const saleFilters = {
          ...(f.from ? { from: f.from } : {}),
          ...(f.to ? { to: f.to } : {}),
          ...(f.status ? { status: f.status } : {}),
          ...(f.partyName ? { customerId: f.partyName } : {}),
        };
        addToDownloads({
          ...res.data.response,
          type: "sales",
          filters: saleFilters,
        });
        toast.success("Sale report generated!");
      } else {
        toast.info(res.data.message || "No report found for the given filters");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to generate sale report",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePurchaseReport = async () => {
    setLoading(true);
    try {
      const params = {};
      const f = filters.purchase;
      if (f.partyId) params.partyId = f.partyId;
      if (f.from) params.from = f.from;
      if (f.to) params.to = f.to;
      if (f.mode) params.mode = f.mode;
      if (f.status) params.status = f.status;

      const res = await getPurchaseReport(params);
      if (res.data.status) {
        triggerDownload(res.data.response.csv, res.data.response.fileName);
        addToDownloads({ ...res.data.response, type: "purchases", filters: f });
        toast.success("Purchase report generated!");
      } else {
        toast.info(res.data.message || "No report found for the given filters");
      }
    } catch (err) {
      toast.error("Failed to generate purchase report");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateProductReport = async () => {
    setLoading(true);
    try {
      const params = {};
      const f = filters.product;
      if (f.from) params.from = f.from;
      if (f.to) params.to = f.to;

      const res = await getItemReport(params);
      if (res.data.status) {
        triggerDownload(res.data.response.csv, res.data.response.fileName);
        addToDownloads({ ...res.data.response, type: "items", filters: f });
        toast.success("Item report generated!");
      } else {
        toast.info(res.data.message || "No report found for the given filters");
      }
    } catch (err) {
      toast.error("Failed to generate item report");
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePartyReport = async () => {
    setLoading(true);
    try {
      const params = {};
      const f = filters.party;
      // Prefer explicit party selection; fallback to name search
      if (f.partyId) params.partyId = f.partyId;
      else if (f.name) params.search = f.name;
      if (f.partyType) params.type = f.partyType;

      const res = await getPartyReport(params);
      if (res.data.status) {
        triggerDownload(res.data.response.csv, res.data.response.fileName);
        const partyFilters = {
          ...(f.partyId ? { partyId: f.partyId } : {}),
          ...(f.name ? { search: f.name } : {}),
          ...(f.partyType ? { type: f.partyType } : {}),
        };
        addToDownloads({
          ...res.data.response,
          type: "parties",
          filters: partyFilters,
        });
        toast.success("Party report generated!");
      } else {
        toast.info(res.data.message || "No report found for the given filters");
      }
    } catch (err) {
      toast.error("Failed to generate party report");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLedgerReport = async () => {
    setLoading(true);
    try {
      const params = {};
      const f = filters.ledger;
      if (f.partyId) params.partyId = f.partyId;
      if (f.type) params.type = f.type;
      if (f.from) params.from = f.from;
      if (f.to) params.to = f.to;

      const res = await getledger(params);
      if (res.data.status) {
        triggerDownload(res.data.response.csv, res.data.response.fileName);
        addToDownloads({ ...res.data.response, type: "ledgers", filters: f });
        toast.success("Ledger report generated!");
      } else {
        toast.info(res.data.message || "No report found for the given filters");
      }
    } catch (err) {
      toast.error("Failed to generate ledger report");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateExpenseReport = async () => {
    setLoading(true);
    try {
      const params = {};
      const f = filters.expense;
      if (f.from) params.from = f.from;
      if (f.to) params.to = f.to;
      if (f.categories) params.categoryId = f.categories;

      const res = await getExpense(params);
      if (res.data.status) {
        triggerDownload(res.data.response.csv, res.data.response.fileName);
        addToDownloads({ ...res.data.response, type: "expenses", filters: f });
        toast.success("Expense report generated!");
      } else {
        toast.info(res.data.message || "No report found for the given filters");
      }
    } catch (err) {
      toast.error("Failed to generate expense report");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (report) => {
    setDownloadingFile(report._id);
    try {
      const token = localStorage.getItem("User_Token");
      const fileNameParam = encodeURIComponent(
        report.fileName || report.title || "",
      );
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/report/downloadReport?fileName=${fileNameParam}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = report.fileName || report.title || "report.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Downloaded successfully!");
    } catch (err) {
      toast.error("Download failed");
    } finally {
      setDownloadingFile(null);
    }
  };

  // Filter Components (Clean & Reusable)
  const FilterSection = ({ type }) => {
    const f = filters[type];
    const update = (key, value) =>
      setFilters((prev) => ({
        ...prev,
        [type]: { ...prev[type], [key]: value },
      }));
    const clear = () => setFilters((prev) => ({ ...prev, [type]: {} }));

    const commonProps = { disabled: loading };

    const isFilterApplied = (obj) => {
      if (!obj) return false;
      return Object.values(obj).some(
        (v) => v !== undefined && v !== null && String(v).trim() !== "",
      );
    };

    switch (type) {
      case "sale":
        return (
          <div className="row g-3 align-items-end">
            <DateRange
              value={{ from: f.from, to: f.to }}
              onChange={(k, v) => update(k, v)}
            />
            <StatusSelect
              label="Status"
              value={f.status}
              onChange={(v) => update("status", v)}
              options={["", "paid", "not_paid"]}
              labels={["All", "Paid", "Not Paid"]}
            />
            <PartySelect
              parties={parties}
              value={f.partyName}
              onChange={(v) => update("partyName", v)}
            />
            <ActionButtons
              onGenerate={() => generateReport(REPORT_TYPES.SALE)}
              onClear={clear}
              loading={loading}
              label="Generate"
              showClear={isFilterApplied(f)}
            />
          </div>
        );

      case "purchase":
        return (
          <div className="row g-3 align-items-end">
            <DateRange
              value={{ from: f.from, to: f.to }}
              onChange={(k, v) => update(k, v)}
            />
            <PartySelect
              parties={parties}
              value={f.partyId}
              onChange={(v) => update("partyId", v)}
            />
            <PaymentModeSelect
              value={f.mode}
              onChange={(v) => update("mode", v)}
            />
            <PaymentStatusSelect
              value={f.status}
              onChange={(v) => update("status", v)}
            />
            <ActionButtons
              onGenerate={() => generateReport(REPORT_TYPES.PURCHASE)}
              onClear={clear}
              loading={loading}
              showClear={isFilterApplied(f)}
            />
          </div>
        );

      case "ledger":
        return (
          <div className="row g-3 align-items-end">
            <DateRange
              value={{ from: f.from, to: f.to }}
              onChange={(k, v) => update(k, v)}
            />
            <PartySelect
              parties={parties}
              value={f.partyId}
              onChange={(v) => update("partyId", v)}
            />
            <ActionButtons
              onGenerate={() => generateReport(REPORT_TYPES.LEDGER)}
              onClear={clear}
              loading={loading}
              label="Generate"
              showClear={isFilterApplied(f)}
            />
          </div>
        );

      case "expense":
        return (
          <div className="row g-3 align-items-end">
            <DateRange
              value={{ from: f.from, to: f.to }}
              onChange={(k, v) => update(k, v)}
            />
            <ActionButtons
              onGenerate={() => generateReport(REPORT_TYPES.EXPENSE)}
              onClear={clear}
              loading={loading}
              showClear={isFilterApplied(f)}
            />
          </div>
        );

      case "party":
        return (
          <div className="row g-3 align-items-end">
            <PartySelect
              parties={parties}
              value={f.partyId}
              onChange={(v) => update("partyId", v)}
            />
            {/* <div className="col-12 col-md-4">
              <label className="form-label fw-semibold">Name</label>
              <input
                type="text"
                className="form-control"
                value={f.name || ""}
                onChange={(e) => update("name", e.target.value)}
              />
            </div> */}
            <PartyTypeSelect
              value={f.partyType || ""}
              onChange={(v) => update("partyType", v)}
            />
            <ActionButtons
              onGenerate={() => generateReport(REPORT_TYPES.PARTY)}
              onClear={clear}
              loading={loading}
              showClear={isFilterApplied(f)}
            />
          </div>
        );

      case "product":
        return (
          <div className="row g-3 align-items-end">
            <DateRange
              value={{ from: f.from, to: f.to }}
              onChange={(k, v) => update(k, v)}
            />
            <ActionButtons
              onGenerate={() => generateReport(REPORT_TYPES.PRODUCT)}
              onClear={clear}
              loading={loading}
              label="Generate "
              showClear={isFilterApplied(f)}
            />
          </div>
        );

      default:
        return null;
    }
  };

  const PartySelect = ({ parties, value, onChange }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [search, setSearch] = useState("");
    const dropdownRef = useRef(null);

    const filtered = parties.filter((p) =>
      p.name.toLowerCase().includes(search.toLowerCase()),
    );

    const selectedParty = parties.find((p) => p._id === value);

    // CLOSE DROPDOWN WHEN CLICK OUTSIDE
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setShowDropdown(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    return (
      <div className="col-12 col-md-4 position-relative" ref={dropdownRef}>
        <label className="form-label fw-semibold">Party</label>

        <button
          type="button"
          className="btn btn-outline-secondary w-100 text-start"
          onClick={() => setShowDropdown(!showDropdown)}
        >
          {selectedParty ? selectedParty.name : "Select Party"}
        </button>

        {showDropdown && (
          <div
            className="position-absolute w-100 shadow border rounded bg-white mt-1"
            style={{ zIndex: 1000 }}
          >
            {/* Search */}
            <div className="p-2 border-bottom">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search party..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>

            {/* Party list */}
            <div style={{ maxHeight: "250px", overflowY: "auto" }}>
              <div
                className="px-3 py-2 border-bottom"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  onChange("");
                  setShowDropdown(false);
                  setSearch("");
                }}
              ></div>

              {filtered.map((p) => (
                <div
                  key={p._id}
                  className="px-3 py-2 border-bottom"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    onChange(p._id);
                    setShowDropdown(false);
                    setSearch("");
                  }}
                >
                  {p.name}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const StatusSelect = ({ label, value, onChange, options, labels }) => (
    <div className="col-12 col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <select
        className="form-select"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((val, i) => (
          <option key={val} value={val}>
            {labels[i]}
          </option>
        ))}
      </select>
    </div>
  );

  const PaymentModeSelect = ({ value, onChange }) => (
    <StatusSelect
      label="Payment Mode"
      value={value}
      onChange={onChange}
      options={["", "cash", "card"]}
      labels={["All", "Cash", "Card"]}
    />
  );

  const PaymentStatusSelect = ({ value, onChange }) => (
    <StatusSelect
      label="Payment Status"
      value={value}
      onChange={onChange}
      options={["", "paid", "not_paid"]} // values sent to backend
      labels={["All", "Paid", "Not Paid"]} // what user sees
    />
  );

  const PartyTypeSelect = ({ value, onChange }) => (
    <div className="col-12 col-md-4">
      <label className="form-label fw-semibold">Party Type</label>
      <select
        className="form-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">All</option>
        <option value="customer">Customer</option>
        <option value="vendor">Vendor</option>
        <option value="both">Both</option>
      </select>
    </div>
  );

  const ActionButtons = ({
    onGenerate,
    onClear,
    loading,
    label = "Generate",
    showClear = true,
  }) => (
    <div className="col-12 d-flex gap-2 mt-3">
      {showClear && onClear ? (
        <button
          className="btn btn-secondary"
          onClick={onClear}
          style={{ minWidth: 100 }}
        >
          Clear
        </button>
      ) : null}
      <button
        className="btn btn-primary"
        onClick={onGenerate}
        disabled={loading}
        style={{ minWidth: 140 }}
      >
        {loading ? <>Generating...</> : label}
      </button>
    </div>
  );

  // ============================
  // Render
  // ============================
  return (
    <section id="main">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="custom-title mb-0">Reports</h4>
      </div>

      <div className="card">
        <div className="card-header">
          <ul className="nav nav-tabs card-header-tabs">
            <TabButton
              label="Reports"
              icon="bi-file-earmark-text"
              active={activeTab === "reports"}
              onClick={() => setActiveTab("reports")}
            />
            <TabButton
              label="Downloads"
              icon="bi-download"
              active={activeTab === "downloads"}
              onClick={() => setActiveTab("downloads")}
            />
          </ul>
        </div>

        <div className="card-body">
          <ToastContainer />
          <style>
            {`
  /* Remove the default arrow when collapsed (normal blue arrow) */
  .accordion-button.collapsed::after {
    filter: none;
  }

  /* Make the default arrow white when active */
  .accordion-button:not(.collapsed)::after {
    filter: brightness(0) invert(1);
  }
`}
          </style>

          {activeTab === "reports" && (
            <div className="accordion">
              {REPORT_ACCORDIONS.map((acc) => (
                <AccordionItem
                  key={acc.id}
                  {...acc}
                  isOpen={activeAccordion === acc.id}
                  onToggle={() =>
                    setActiveAccordion(
                      activeAccordion === acc.id ? null : acc.id,
                    )
                  }
                >
                  <FilterSection type={acc.type.toLowerCase()} />
                </AccordionItem>
              ))}
            </div>
          )}

          {activeTab === "downloads" && (
            <DownloadsTable
              downloads={downloads}
              loading={loading}
              downloadingFile={downloadingFile}
              onDownload={handleDownload}
              parties={parties}
            />
          )}
        </div>
      </div>
    </section>
  );
}

// Reusable UI Components
const TabButton = ({ label, icon, active, onClick }) => (
  <li className="nav-item">
    <button className={`nav-link ${active ? "active" : ""}`} onClick={onClick}>
      <i className={`bi ${icon} me-2`}></i>
      {label}
    </button>
  </li>
);

const AccordionItem = ({ title, icon, isOpen, onToggle, children }) => (
  <div className="accordion-item mb-3 border rounded shadow-sm">
    <h2 className="accordion-header">
      <button
        className={`accordion-button ${isOpen ? "" : "collapsed"} fw-medium`}
        style={{
          backgroundColor: isOpen ? "#012970" : "",
          color: isOpen ? "white" : "",
        }}
        onClick={onToggle}
      >
        <i className={`${icon} me-2`}></i> {title}
      </button>
    </h2>
    <div className={`accordion-collapse collapse ${isOpen ? "show" : ""}`}>
      <div className="accordion-body pt-3">{children}</div>
    </div>
  </div>
);

const DownloadsTable = ({
  downloads,
  loading,
  downloadingFile,
  onDownload,
  parties = [],
}) => (
  <div className="table-responsive">
    {loading ? (
      <Loading />
    ) : downloads.length === 0 ? (
      <div className="text-center py-5">
        <i className="bi bi-inbox display-1 text-muted"></i>
        <p className="text-muted">No downloads yet.</p>
      </div>
    ) : (
      <table className="table table-hover align-middle">
        <thead className="table-light">
          <tr>
            <th>#</th>
            <th>Report</th>
            <th>Created At</th>
            <th className="text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          {downloads.map((d, i) => (
            <tr key={d._id}>
              <td>{i + 1}</td>
              <td>
                <div
                  style={{ fontSize: "1.08rem", fontWeight: 700 }}
                  className="fw-semibold"
                >
                  {d.title}
                </div>
                {d.filters && Object.keys(d.filters).length > 0 && (
                  <div className="small text-muted mt-1">
                    {formatFiltersInline(d.filters, parties)}
                  </div>
                )}
              </td>
              <td>{moment(d.date).format("DD MMM, YYYY")}</td>
              <td className="text-center">
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => onDownload(d)}
                  disabled={downloadingFile === d._id}
                >
                  {downloadingFile === d._id ? "Downloading..." : "Download"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export default headerLayoutWrapper(Reports);
